/**
 * Stable Salesforce Canvas auth error codes and helpers for the SPA.
 * Keep in sync with packages/common/src/utils/canvasAuthErrors.ts (published
 * via @labrav/common) and backend/types/canvasAuth.ts.
 *
 * Host cannot import these from the published package: Vitest and `yarn start`
 * resolve `@labrav/common` from CodeArtifact, which lags this PR. The host
 * CanvasAuthService is also a separate singleton from the package copy because
 * only the host persists the Canvas token for MFE interceptors.
 */

export const CANVAS_AUTH_ERROR_QUERY_PARAM = 'canvas_auth_error'

export const CANVAS_AUTH_ERROR_CODES = [
  'signed_request_missing',
  'signature_invalid',
  'organization_id_missing',
  'partner_unmapped',
  'crm_id_missing',
  'location_url_missing',
  'instance_url_missing',
  'client_properties_missing',
  'target_origin_invalid',
  'target_url_invalid',
  'canvas_param_target_url_invalid',
  'internal_error',
] as const

export type CanvasAuthErrorCode = (typeof CANVAS_AUTH_ERROR_CODES)[number]

export const EXCHANGE_TOKEN_ABSENT_CLIENT = 'exchange_token_absent_client'

/** Salesforce Canvas handshake is dead; the Opportunity page must be reloaded. */
export const CANVAS_SESSION_EXPIRED = 'canvas_session_expired'

/** Dispatched by CanvasAuthService when refreshSignedRequest can no longer prove the session. */
export const CANVAS_SESSION_EXPIRED_EVENT = 'canvas-session-expired'

const CANVAS_AUTH_ERROR_MESSAGES: Record<CanvasAuthErrorCode, string> = {
  signed_request_missing:
    'Salesforce did not send a Canvas signed request. Reload this page from Salesforce.',
  signature_invalid:
    'Canvas signature verification failed. Ask your admin to check the Connected App consumer secret.',
  organization_id_missing:
    'Canvas context is missing the Salesforce organization ID. Reload from Salesforce.',
  partner_unmapped:
    'This Salesforce org is not connected to a Labra partner. Ask your admin to complete CRM setup.',
  crm_id_missing:
    'No CRM connection was found for this partner. Ask your admin to complete CRM setup.',
  location_url_missing:
    'Canvas context is missing location URL. Reload this page from Salesforce.',
  instance_url_missing:
    'Canvas context is missing Salesforce instance URL. Reload this page from Salesforce.',
  client_properties_missing:
    'Canvas context is incomplete. Reload this page from Salesforce.',
  target_origin_invalid:
    'Canvas target origin is invalid. Reload this page from Salesforce.',
  target_url_invalid:
    'Canvas target URL is invalid. Reload this page from Salesforce.',
  canvas_param_target_url_invalid:
    'Canvas navigation target is invalid. Reload this page from Salesforce.',
  internal_error:
    'Canvas authentication failed due to a server error. Please try again from Salesforce.',
}

export function isCanvasAuthErrorCode(
  value: string | null | undefined
): value is CanvasAuthErrorCode {
  return (
    typeof value === 'string' &&
    (CANVAS_AUTH_ERROR_CODES as readonly string[]).includes(value)
  )
}

export function getCanvasAuthErrorFromLocation(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): CanvasAuthErrorCode | null {
  const params = new URLSearchParams(search)
  const code = params.get(CANVAS_AUTH_ERROR_QUERY_PARAM)
  return isCanvasAuthErrorCode(code) ? code : null
}

export function getCanvasAuthErrorMessage(
  code:
    | CanvasAuthErrorCode
    | typeof EXCHANGE_TOKEN_ABSENT_CLIENT
    | typeof CANVAS_SESSION_EXPIRED
    | string
): string {
  if (code === EXCHANGE_TOKEN_ABSENT_CLIENT) {
    return 'Canvas session token is missing. Reload this page from Salesforce Canvas (do not refresh the iframe alone).'
  }
  if (code === CANVAS_SESSION_EXPIRED) {
    return 'Canvas session expired. Reload this Opportunity page from Salesforce (do not refresh the iframe alone).'
  }
  if (isCanvasAuthErrorCode(code)) {
    return CANVAS_AUTH_ERROR_MESSAGES[code]
  }
  return 'Failed to authenticate. Please reload the page from Salesforce Canvas.'
}

/**
 * True when we should run Canvas JWT exchange / handshake-error handling.
 * SalesforceContextProvider wraps the whole Auth0 app (app.labra.io included) —
 * never treat a leftover sessionStorage exchange_token on regular Labra routes
 * as Canvas context (that would POST /backend/salesforce/canvas/token).
 */
export function shouldAttemptCanvasAuth(options?: {
  pathname?: string
  search?: string
  /** Kept for callers/tests; ignored once the path is confirmed as Canvas. */
  hasExchangeToken?: boolean
  useAuth0DevMode?: boolean
}): boolean {
  const useAuth0DevMode =
    options?.useAuth0DevMode ??
    (typeof import.meta !== 'undefined' &&
      // Vite injects this in browser builds; absent in Node unit tests.
      (import.meta as ImportMeta & { env?: Record<string, string> }).env
        ?.VITE_SALESFORCE_USE_AUTH0 === 'true')
  if (useAuth0DevMode) return true

  const search =
    options?.search ??
    (typeof window !== 'undefined' ? window.location.search : '')
  if (getCanvasAuthErrorFromLocation(search)) return true

  const pathname =
    options?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '')
  // Canvas handshake always lands under /external-apps/salesforce/...
  // Regular Auth0 product routes must never call /canvas/token — including when
  // a stale exchange_token remains in sessionStorage from a prior Canvas visit.
  return pathname.includes('/external-apps/salesforce')
}
